import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { usePagedCursor, type PagedCursor } from '@app/api/hooks/usePagedCursor';
import type { components } from '@app/api/types';
import { get, withPath, type ApiData } from '@app/api/typed';
import type { Causa } from './causa';
import type { Decision } from './vocabulario';
import { t } from '@app/i18n';

export type DecisionPage = ApiData<'/api/v1/decisions'>;
export type DecisionDetail = ApiData<'/api/v1/decisions/{request_id}'>;
export type Identity = components['schemas']['Identity'];

/** Períodos das pílulas de T16, em minutos. */
export const PERIODOS = [
  { id: '1h', minutos: 60 },
  { id: '24h', minutos: 60 * 24 },
  { id: '7d', minutos: 60 * 24 * 7 },
  // 30 d (Lote 2b): existe porque a fonte passou a cobrir tanto. Com o histórico
  // no control plane a janela é a retenção configurada, não as 24 h da memória do
  // executor — e uma pílula que a fonte cobre e a tela não oferece é o DEF-35 ao
  // contrário.
  { id: '30d', minutos: 60 * 24 * 30 },
  // PRD §4 T16: "personalizado" — o início da janela vem de um campo de data/hora
  // (`DecisionsFiltersValue.desde`); `minutos` não se aplica.
  { id: 'custom', minutos: null },
] as const;

/**
 * A pílula cabe na janela que a fonte cobre? (DEF-35)
 *
 * ## O defeito que isto conserta
 *
 * A tela oferecia «7 d» sobre uma fonte que publicava `window_max_min: 1440`
 * (24 h). Clicar em 7 d devolvia 24 h de decisões com um aviso discreto de
 * janela ajustada — e a leitura natural daquilo é «não houve nada na semana»,
 * que é o oposto de «não perguntamos pela semana». Numa superfície de auditoria,
 * a diferença é tudo.
 *
 * ## Por que a resposta é `true` enquanto a janela é desconhecida
 *
 * `window_max_min` chega com a PRIMEIRA página. Antes dela nada se sabe, e
 * desabilitar por precaução esconderia períodos que a fonte cobre — trocaria um
 * erro por outro, e este seria permanente para quem abre a tela com a lista
 * vazia. Enquanto não se sabe, oferece-se; assim que a resposta chega, o que não
 * cabe fica desabilitado, com o motivo.
 *
 * `custom` nunca é bloqueado aqui: quem digita uma data mais antiga que a janela
 * recebe o ajuste e o aviso (`since_adjusted`), que é a mesma informação dita no
 * momento em que ela é verdadeira.
 */
export function periodFitsWindow(id: PeriodID, windowMaxMin: number | null | undefined): boolean {
  if (!windowMaxMin || windowMaxMin <= 0) return true;
  const period = PERIODOS.find((p) => p.id === id);
  if (!period || period.minutos === null) return true;
  return period.minutos <= windowMaxMin;
}

/**
 * A janela da fonte, em texto de tela: horas até dois dias, dias daí em diante.
 *
 * Existe porque o número mudou de ordem de grandeza. Com o executor a janela era
 * 1440 min — «24 h» —, e com a retenção do control plane ela é 43 200 min: «720 h»
 * é aritmeticamente certo e ninguém lê como um mês.
 */
export function windowLabel(windowMaxMin: number | null | undefined): string {
  if (!windowMaxMin || windowMaxMin <= 0) return '—';
  const hours = Math.round(windowMaxMin / 60);
  if (hours < 48) return t('decisions.window.hours', { hours });
  return t('decisions.window.days', { days: Math.round(hours / 24) });
}

/**
 * Rótulo do período, do catálogo. Fora da tabela porque o `rotulo` ali seria
 * avaliado na importação e ficaria preso ao idioma daquele instante.
 */
export function periodLabel(id: PeriodID): string {
  return t(`decisions.filters.period.${id}`);
}

export type PeriodID = (typeof PERIODOS)[number]['id'];

export interface FilterDecisions {
  /** Instante inicial da janela, já resolvido (RFC 3339). */
  since: string;
  /**
   * O `before` da primeira página: o instante em que a lista abriu, ou o do último «Atualizar» (RFC 3339). A lista
   * vem das mais novas para as mais antigas (D10, aim-6ph.4), e este é o topo dela.
   */
  before: string;
  veredito?: Decision['verdict'];
  /**
   * Por que negou (`causa`). Vai **à fonte**, como identidade e tool: o executor
   * filtra por ela desde que copia a causa do PDP.
   */
  cause?: Causa;
  identityId?: string;
  tool?: string;
  /**
   * aim-5wc — «Ver as decisões desta conversa» in the Chat: the person (`sub`) and the end of the window
   * (`until`, RFC 3339). They go to the backend, which checks the page — the executor does not filter by them.
   */
  sub?: string;
  until?: string;
  limit?: number;
  /**
   * Traz também as decisões do Simulador (T18) e da lente «Ver como» do mapa
   * (T19). Ausente/`false` = o corte, que é o padrão da fonte.
   *
   * O corte existe porque as duas telas perguntam ao **PDP de verdade** — é o
   * que faz a resposta valer — e o PDP loga toda avaliação. Sem ele, uma
   * demonstração de doze cliques põe doze decisões que não aconteceram nesta
   * lista, com a tipografia do tráfego.
   */
  includeSimulated?: boolean;
  /**
   * Traz também as chamadas de controle do executor: a descoberta dele pelo gateway (`initialize`, `tools/list`, o
   * card A2A). Ausente/`false` = o corte, que é o padrão do backend (D1, aim-6ph.8).
   */
  includeControlCalls?: boolean;
}

export const decisionsKeys = {
  lista: (filter: FilterDecisions) => ['decisions', filter] as const,
  detalhe: (requestId: string) => ['decisions', 'detalhe', requestId] as const,
  identidadesElegiveis: (withContract: boolean) => ['identities', { comContrato: withContract }] as const,
};

/** `agora − minutos`, em RFC 3339 — o `since` inicial da janela. */
export function sinceNow(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

/** Os parâmetros dos filtros de uma página, sem o cursor. */
function filterParams(filter: FilterDecisions) {
  return {
    ...(filter.limit ? { limit: filter.limit } : {}),
    // `verdict`, não `veredito`: é o nome do parâmetro no contrato, e o
    // backend só lê esse. Enquanto a tela mandava `veredito` o filtro de
    // veredito de T16 não chegava a lugar nenhum — a lista continuava
    // inteira e o teste que existia afirmava a query errada, não o efeito.
    ...(filter.veredito ? { verdict: filter.veredito } : {}),
    ...(filter.cause ? { cause: filter.cause } : {}),
    ...(filter.identityId ? { identity_id: filter.identityId } : {}),
    ...(filter.tool ? { tool: filter.tool } : {}),
    ...(filter.sub ? { sub: filter.sub } : {}),
    ...(filter.until ? { until: filter.until } : {}),
    // Só quando ligado: o padrão do contrato já é excluir, e mandar
    // `include_simulated=false` a cada página trocaria a chave da query
    // sem trocar a resposta.
    ...(filter.includeSimulated ? { include_simulated: true } : {}),
    ...(filter.includeControlCalls ? { include_control_calls: true } : {}),
  };
}

/** O tamanho da página que a busca de 10 s pede: o `limit` padrão do contrato, e o `{n}` de «Mais de {n}». */
export const NEWER_PAGE = 50;

/** De quanto em quanto tempo a tela pergunta se chegaram decisões novas (D4 do mrolim). */
export const NEWER_INTERVAL_MS = 10_000;

/** As decisões novas que a faixa «N novas» anuncia. */
export interface NewerDecisions {
  /** Quantas chegaram e ainda não estão na lista. */
  count: number;
  /** `true` quando chegaram mais que a página da busca: a faixa diz «Mais de {n}». */
  many: boolean;
  /** «Mostrar»: põe as novas no topo e mantém as que já estavam carregadas (decisão T3 do protótipo). */
  show: () => void;
}

export interface DecisionsList extends PagedCursor<DecisionPage, Decision> {
  newer: NewerDecisions;
}

/**
 * Lista de decisões (T16, FR-096), das mais novas para as mais antigas (D4 e D10 do mrolim, aim-6ph.4).
 *
 * A tela **não sabe** de onde a página veio, e é esse o ponto: desde o Lote 2b
 * ela pode vir do histórico do control plane (retenção configurada) ou do
 * executor (buffer de 24 h), e o backend escolhe. O `source` da resposta diz qual
 * foi, para a tela escrever «fonte …» — mas o formato e o cursor são os mesmos, e
 * nenhum componente ramifica por origem.
 *
 * ## Os dois cursores do contrato, e quem usa cada um
 *
 * A lista anda PARA TRÁS pelo modo decrescente: a primeira página pede `before` = o instante em que a tela abriu, e
 * «Mostrar decisões mais antigas» pede `before = next_before`, opaco, como veio: ele marca a última linha pelo par
 * instante e `request_id`, então o cursor não repete nem pula decisão; `next_before` nulo é o fim do período.
 *
 * As NOVAS chegam pelo modo para a frente: a cada 10 s, `since` = a decisão mais nova da lista, com os mesmos
 * filtros. `since` é inclusivo, e a decisão da borda volta repetida; a contagem a descarta pelo `request_id`. A busca
 * para enquanto o detalhe (T17) está aberto e enquanto a aba do navegador está escondida, e a lista não muda sozinha:
 * só «Mostrar» põe as novas no topo.
 */
export function useDecisions(filter: FilterDecisions, { paused = false } = {}): DecisionsList {
  const pages = usePagedCursor<DecisionPage, Decision>({
    queryKey: decisionsKeys.lista(filter),
    since: filter.before,
    fetchPage: (before) =>
      get('/api/v1/decisions', {
        params: { since: filter.since, before, ...filterParams(filter) },
      }),
    next: (page) => page.next_before ?? undefined,
    itens: (page) => page.items ?? [],
    chave: (decisao) => decisao.request_id,
    ordenar: moreFirstRecentes,
  });

  // As novas que «Mostrar» aceitou, presas à lista em que foram aceitas: trocar um filtro ou atualizar é outra lista,
  // e as de antes não podem vazar para ela.
  const listKey = JSON.stringify(decisionsKeys.lista(filter));
  const [accepted, setAccepted] = useState<{ key: string; items: Decision[] }>({ key: listKey, items: [] });
  const acceptedHere = useMemo(
    () => (accepted.key === listKey ? accepted.items : []),
    [accepted, listKey],
  );
  const itens = useMemo(() => {
    const byID = new Map<string, Decision>();
    for (const decision of [...acceptedHere, ...pages.itens]) byID.set(decision.request_id, decision);
    return [...byID.values()].sort(moreFirstRecentes);
  }, [acceptedHere, pages.itens]);

  const newest = itens[0]?.ts ?? null;
  // A primeira pergunta é 10 s depois de a lista mudar de topo, e não na hora: a lista acabou de ser lida.
  // A chave do relógio é a lista E o topo: outro filtro com o mesmo topo também espera os 10 s.
  const armKey = `${listKey}|${newest ?? ''}`;
  const [armedFor, setArmedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!newest) return undefined;
    const timer = setTimeout(() => setArmedFor(armKey), NEWER_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [newest, armKey]);
  const poll = useQuery<DecisionPage, ApiError>({
    queryKey: ['decisions', 'newer', filter, newest],
    queryFn: () =>
      get('/api/v1/decisions', {
        params: { ...filterParams(filter), since: newest!, limit: NEWER_PAGE },
      }),
    enabled: Boolean(newest) && armedFor === armKey && !paused,
    refetchInterval: NEWER_INTERVAL_MS,
    // A aba escondida não pergunta: é o padrão do react-query, escrito aqui porque é regra da tela.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const loaded = useMemo(() => new Set(itens.map((d) => d.request_id)), [itens]);
  const fresh = useMemo(
    // `Cursor.items` é `unknown[]` no contrato gerado; a página de decisões o especializa, e o estreitamento fica aqui.
    () => ((poll.data?.items ?? []) as Decision[]).filter((d) => !loaded.has(d.request_id)),
    [poll.data, loaded],
  );
  const pollPage = poll.data;
  const many = Boolean(pollPage?.next_since && pollPage.next_since !== pollPage.since);

  return {
    ...pages,
    itens,
    newer: {
      count: fresh.length,
      many,
      show: () => setAccepted({ key: listKey, items: [...acceptedHere, ...fresh] }),
    },
  };
}

/** Mais recentes primeiro (FR-097). */
export function moreFirstRecentes(a: Decision, b: Decision): number {
  return b.ts.localeCompare(a.ts);
}

/**
 * Junta páginas de decisões: **dedup por `request_id`** e ordenação por `ts`
 * decrescente (mais recentes primeiro, FR-097).
 */
export function decisionsUnicas(pages: DecisionPage[] | undefined): Decision[] {
  const byID = new Map<string, Decision>();
  for (const page of pages ?? []) {
    for (const decisao of page.items ?? []) byID.set(decisao.request_id, decisao);
  }
  return [...byID.values()].sort(moreFirstRecentes);
}

/** Detalhe da decisão (T17, FR-099). 404 `detalhe_indisponivel` fora da janela. */
export function useDecision(requestId: string | null): UseQueryResult<DecisionDetail, ApiError> {
  return useQuery<DecisionDetail, ApiError>({
    queryKey: decisionsKeys.detalhe(requestId ?? ''),
    queryFn: () =>
      get(withPath('/api/v1/decisions/{request_id}', { request_id: requestId! }) as never),
    enabled: Boolean(requestId),
    retry: false,
  });
}

/**
 * Identidades para o combobox — filtro de T16 (todas) e simulador T18
 * (`com_contrato=true`, só as que têm contrato ativo).
 */
export function useIdentities(withContract = false): UseQueryResult<Identity[], ApiError> {
  return useQuery<Identity[], ApiError>({
    queryKey: decisionsKeys.identidadesElegiveis(withContract),
    queryFn: async () => {
      const page = await get('/api/v1/identities', {
        params: { size: 200, ...(withContract ? { com_contrato: true } : {}) },
      });
      // `Paginacao.itens` é `unknown[]` no contrato gerado e a especialização por
      // operação entra por interseção — o TS resolve o acesso indexado pelo lado
      // genérico, então o estreitamento fica aqui, num ponto só.
      return (page.items ?? []) as Identity[];
    },
    staleTime: 60_000,
  });
}
